import type { NormalizedStatus, OrderedStatusExtension } from './types';

/**
 * Produces an ordered list of status items based on a predefined sort mapping

 * How it works:
 *  - Builds lookup table from the provided array (id → order index)
 *  - Filters out any statuses where `count` is 0
 *  - Sorts the remaining statuses based on their code
 *
 * Example:
 *  [
 *    { code: "completed", count: 5, name: "Completed" },
 *    { code: "received", count: 2, name: "Received" },
 *    { code: "pending", count: 0, name: "Pending" }
 *  ]
 *   returns:
 *  [
 *    { code: "received", ... }, // order = 1
 *    { code: "completed", ... } // order = 8
 *  ]
 *
 * @param order     Array with id + order, like COLLECTION_ORDER_STATUSES or PAYOUT_STATUSES
 * @param statuses  Normalized BE status list
 * @returns         Sorted and filtered array
 */

export function orderStatusesFromMapping(
  order: ReadonlyArray<OrderedStatusExtension>,
  statuses: ReadonlyArray<NormalizedStatus>
) {
  const orderIndex: Record<string, number> = Object.fromEntries(
    order.map((d) => [d.id, d.order])
  );

  return statuses
    .filter((s) => s.count > 0)
    .sort((a, b) => orderIndex[a.code] - orderIndex[b.code]);
}
